import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  TestTube,
  Calendar,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  ShieldCheck,
  X,
  FileCheck,
  Building2,
  Home,
  Clock,
  Sparkles,
  MapPin,
  CheckCircle2,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import {
  medicalRecordsApi,
  examsHubApi,
  type OrderQuotesResponse,
  type LabQuoteItem,
} from '@/api';
import type { ExamItem } from '@/api/http-client';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { MeMedStatusBadge } from '@/components/memed/MeMedStatusBadge';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/providers/ToastProvider';

interface ExamUI {
  id: string;
  doctorName: string;
  doctorCrm: string;
  specialty: string;
  patientName: string;
  examType: string;
  exams: ExamItem[];
  instructions?: string;
  requestedAt: string;
  resultUrl?: string;
  resultAt?: string;
  status: 'REQUESTED' | 'PENDING_RESULT' | 'COMPLETED';
  pdfUrl?: string;
  isDigital?: boolean;
  memedId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
}

interface ExamsResponse {
  exams: ExamUI[];
  total: number;
  page: number;
  pageSize: number;
}

function useSafeToast() {
  try {
    return useToast();
  } catch {
    return {
      toast: () => {},
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
    };
  }
}

export function ExamsPage() {
  const queryClient = useQueryClient();
  const { toast } = useSafeToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'REQUESTED' | 'COMPLETED'>('ALL');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  // Estado do Modal de Cotação e Agendamento Laboratorial
  const [quoteExam, setQuoteExam] = useState<ExamUI | null>(null);
  const [selectedLabId, setSelectedLabId] = useState<string | null>(null);
  const [collectionType, setCollectionType] = useState<'DOMICILIAR' | 'POSTO_COLETA'>('DOMICILIAR');
  const [scheduledDate, setScheduledDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(8, 0, 0, 0);
    return tomorrow.toISOString().slice(0, 16);
  });
  const [address, setAddress] = useState({
    cep: '01310-100',
    logradouro: 'Av. Paulista',
    numero: '1000',
    complemento: 'Apto 42',
    bairro: 'Bela Vista',
    cidade: 'São Paulo',
    uf: 'SP',
  });
  const [scheduleSuccessProtocol, setScheduleSuccessProtocol] = useState<string | null>(null);

  // Consulta de Cotação de Laboratórios para o Exame Selecionado
  const {
    data: quoteData,
    isLoading: isLoadingQuote,
    isError: isErrorQuote,
  } = useQuery<OrderQuotesResponse>({
    queryKey: ['lab-quotes', quoteExam?.id],
    queryFn: () => examsHubApi.quoteLaboratories(quoteExam!.id),
    enabled: Boolean(quoteExam?.id),
  });

  // Mutação para Agendamento do Exame
  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!quoteExam || !selectedLabId) return;
      return examsHubApi.scheduleExam(quoteExam.id, {
        laboratorioId: selectedLabId,
        tipoColeta: collectionType,
        dataHoraAgendada: new Date(scheduledDate).toISOString(),
        enderecoColeta: collectionType === 'DOMICILIAR' ? address : undefined,
      });
    },
    onSuccess: (res: any) => {
      setScheduleSuccessProtocol(res?.protocolo || 'CONFIRMADO');
      queryClient.invalidateQueries({ queryKey: ['patient', 'exams'] });
      toast({
        title: 'Agendamento confirmado com sucesso!',
        description: `Protocolo: ${res?.protocolo || 'N/A'}. A equipe laboratorial entrará em contato.`,
        variant: 'success',
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao agendar exame',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async (exam: ExamUI) => {
      if (exam.pdfUrl) {
        const link = document.createElement('a');
        link.href = exam.pdfUrl;
        link.target = '_blank';
        link.rel = 'noopener,noreferrer';
        link.download = `pedido_exame_${exam.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      const response = await medicalRecordsApi.downloadExamRequestPdf(exam.id);
      const link = document.createElement('a');
      link.href = `data:${response.mimeType || 'application/pdf'};base64,${response.pdf}`;
      link.download = `pedido_exame_${exam.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'exams'] });
    },
  });

  const { data, isLoading, isError, isFetching, refetch } = useQuery<ExamsResponse, Error>({
    queryKey: ['patient', 'exams', page, filter],
    queryFn: async () => {
      const response = await medicalRecordsApi.getPatientExamRequests(page, 10);
      const list = Array.isArray(response?.data) ? response.data : [];
      return {
        exams: list.map((req: any) => ({
          id: req.id,
          doctorName: req.doctorName || req.doctorId || 'Médico Responsável',
          doctorCrm: req.doctorCrm || '',
          specialty: req.doctorSpecialty || req.specialty || 'Clínica Geral',
          patientName: 'Você',
          examType: (req.exams || []).map((e: ExamItem) => e.examName).join(', ') || 'Exame Laboratorial',
          exams: req.exams || [],
          instructions: req.instructions || '',
          requestedAt: req.createdAt,
          resultUrl: req.resultUrl,
          resultAt: req.resultAt,
          status: (req.status || (req.resultAt ? 'COMPLETED' : 'REQUESTED')) as ExamUI['status'],
          pdfUrl: req.pdfUrl || req.memedPdfUrl || undefined,
          isDigital: req.isDigital !== false,
          memedId: req.memedExamRequestId || req.memedId,
          memedStatus: req.memedStatus,
        })),
        total: response?.meta?.total ?? list.length,
        page: response?.meta?.page ?? page,
        pageSize: response?.meta?.limit ?? 10,
      };
    },
  });

  const handleCopyExamRequest = async (exam: ExamUI) => {
    try {
      const text = [
        `PEDIDO DE EXAME - MEDIKEI SAÚDE`,
        `Médico: ${exam.doctorName} (${exam.doctorCrm})`,
        `Especialidade: ${exam.specialty}`,
        `Data: ${new Date(exam.requestedAt).toLocaleDateString('pt-BR')}`,
        `Exames Solicitados:`,
        exam.exams && exam.exams.length > 0
          ? exam.exams.map((e, idx) => `  ${idx + 1}. ${e.examName}${e.justification ? ` (Indicação: ${e.justification})` : ''}`).join('\n')
          : `  1. ${exam.examType}`,
        exam.instructions ? `Orientações: ${exam.instructions}` : '',
        exam.pdfUrl ? `Link do Pedido Assinado (ICP-Brasil): ${exam.pdfUrl}` : '',
      ]
        .filter(Boolean)
        .join('\n');

      await navigator.clipboard.writeText(text);
      setCopiedId(exam.id);
      setTimeout(() => setCopiedId(null), 3000);
      toast({
        title: 'Pedido copiado para a área de transferência',
        description: 'Você pode colar e enviar no WhatsApp do laboratório.',
        variant: 'success',
      });
    } catch {
      toast({ title: 'Erro ao copiar', variant: 'error' });
    }
  };

  const handleViewPdf = async (exam: ExamUI) => {
    setViewingId(exam.id);
    try {
      if (exam.pdfUrl) {
        window.open(exam.pdfUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const response = await medicalRecordsApi.downloadExamRequestPdf(exam.id);
      const blob = new Blob([Uint8Array.from(atob(response.pdf), (c) => c.charCodeAt(0))], {
        type: response.mimeType || 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast({ title: 'Erro ao abrir visualização do PDF', variant: 'error' });
    } finally {
      setViewingId(null);
    }
  };

  const handleOpenQuoteModal = (exam: ExamUI) => {
    setQuoteExam(exam);
    setSelectedLabId(null);
    setScheduleSuccessProtocol(null);
  };

  const formatDate = (dateStr?: string | Date | null) => {
    if (!dateStr) return 'Data não informada';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const filteredExams = (data?.exams || []).filter((exam) => {
    if (filter === 'REQUESTED' && exam.status === 'COMPLETED') return false;
    if (filter === 'COMPLETED' && exam.status !== 'COMPLETED') return false;

    if (!search.trim()) return true;
    const term = search.toLowerCase();
    const doctorMatch = exam.doctorName?.toLowerCase().includes(term);
    const typeMatch = exam.examType?.toLowerCase().includes(term);
    const examsMatch = (exam.exams || []).some(
      (e) =>
        e.examName?.toLowerCase().includes(term) ||
        e.justification?.toLowerCase().includes(term),
    );
    return doctorMatch || typeMatch || examsMatch;
  });

  const totalPages = data && data.pageSize ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <div className="space-y-6">
      {/* Header com Logo Oficial da Medikei, Contador e Ação Atualizar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-5 sm:p-6 bg-card border border-border rounded-2xl shadow-xs">
        <div className="flex items-center gap-4">
          {/* Logo Oficial da Medikei (Identidade Visual da Landing Page) */}
          <div className="shrink-0">
            <img
              src="/logo.png"
              alt="Medikei Saúde"
              className="h-9 sm:h-10 w-auto drop-shadow-xs dark:brightness-110 transition-all duration-300"
            />
          </div>

          <div className="h-8 w-px bg-border/80 hidden sm:block" />

          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">
                Meus Exames & Diagnósticos
              </h1>
              {data && data.total > 0 && (
                <Badge
                  variant="outline"
                  className="font-bold text-xs bg-primary/10 text-primary border-primary/20"
                >
                  {data.total} pedido{data.total !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
              Hub inteligente de pedidos de exames com certificação ICP-Brasil e comparador de laboratórios parceiros.
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="gap-2 rounded-xl font-semibold shadow-xs hover:bg-muted w-full md:w-auto h-11 shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-primary' : ''}`} />
          <span>Atualizar</span>
        </Button>
      </div>

      {/* Barra de Busca e Filtro de Status */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por médico ou nome do exame..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
              aria-label="Limpar busca"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="px-4 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs"
        >
          <option value="ALL">Todos os Pedidos</option>
          <option value="REQUESTED">Solicitados (Aguardando Coleta)</option>
          <option value="COMPLETED">Concluídos com Laudo</option>
        </select>
      </div>

      {/* Estados de Carregamento, Erro ou Vazio */}
      {isLoading ? (
        <LoadingOverlay message="Carregando pedidos de exames..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar exames" onRetry={() => refetch()} />
      ) : filteredExams.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 sm:p-12 text-center bg-card">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center mx-auto mb-4 shadow-xs">
            <TestTube className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-foreground">Nenhum exame encontrado</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
            {search
              ? 'Não encontramos nenhum exame com os termos pesquisados.'
              : 'Os pedidos de exames laboratoriais e diagnósticos por imagem solicitados nas suas consultas aparecerão aqui.'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-5">
          {filteredExams.map((exam) => {
            const isDownloading = downloadMutation.isPending && downloadMutation.variables?.id === exam.id;
            const isViewing = viewingId === exam.id;
            const isCopied = copiedId === exam.id;
            const examCount = exam.exams?.length || 1;

            return (
              <Card
                key={exam.id}
                className="rounded-2xl border border-border bg-card shadow-xs hover:shadow-md hover:border-primary/40 transition-all overflow-hidden"
              >
                <CardContent className="p-5 sm:p-6 space-y-5">
                  {/* Cabeçalho do Pedido */}
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-border/70">
                    <div className="flex items-start gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/25 flex items-center justify-center text-primary shrink-0 shadow-xs">
                        <TestTube className="h-6 w-6" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-extrabold text-lg text-foreground tracking-tight truncate">
                            {exam.doctorName}
                          </h3>
                          {exam.doctorCrm && (
                            <Badge
                              variant="outline"
                              className="text-[11px] font-semibold text-muted-foreground border-border/80"
                            >
                              {exam.doctorCrm}
                            </Badge>
                          )}
                          <span className="text-sm font-semibold text-primary">
                            {exam.specialty}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-lg border border-border/40">
                            <Calendar className="h-3.5 w-3.5 text-primary" />
                            Solicitado em {formatDate(exam.requestedAt)}
                          </span>

                          <Badge
                            variant="outline"
                            className="text-xs font-bold bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
                          >
                            {examCount} exame{examCount !== 1 ? 's' : ''} no pedido
                          </Badge>

                          {(exam.pdfUrl || exam.memedStatus === 'SIGNED') && (
                            <Badge
                              variant="outline"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                            >
                              <ShieldCheck className="h-3.5 w-3.5" />
                              Assinado ICP-Brasil
                            </Badge>
                          )}

                          {exam.memedStatus && (
                            <MeMedStatusBadge
                              status={exam.memedStatus}
                              memedId={exam.memedId}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação do Pedido */}
                    <div className="flex flex-wrap items-center gap-2 self-start lg:self-center">
                      {/* Botão Principal: Cotar e Agendar */}
                      <Button
                        size="sm"
                        onClick={() => handleOpenQuoteModal(exam)}
                        className="rounded-xl text-xs font-bold gap-1.5 h-10 px-4 bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm shadow-primary/20"
                      >
                        <Sparkles className="h-4 w-4 text-amber-300" />
                        <span>Cotar e Agendar Exames</span>
                      </Button>

                      {/* Copiar Dados */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyExamRequest(exam)}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-10 hover:bg-muted"
                        title="Copiar dados dos exames"
                      >
                        {isCopied ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-emerald-600 font-bold">Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>Copiar</span>
                          </>
                        )}
                      </Button>

                      {/* Visualizar PDF */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewPdf(exam)}
                        disabled={isViewing}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-10 hover:bg-muted"
                      >
                        <ExternalLink className={`h-3.5 w-3.5 ${isViewing ? 'animate-spin' : ''}`} />
                        <span>Visualizar</span>
                      </Button>

                      {/* Baixar PDF */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadMutation.mutate(exam)}
                        disabled={isDownloading}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-10 hover:bg-muted"
                      >
                        <Download className={`h-3.5 w-3.5 ${isDownloading ? 'animate-spin' : ''}`} />
                        <span>Baixar Pedido</span>
                      </Button>

                      {/* Ver Resultado se Concluído */}
                      {exam.status === 'COMPLETED' && exam.resultUrl && (
                        <a
                          href={exam.resultUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-xs transition-colors h-10"
                        >
                          <FileCheck className="h-4 w-4" />
                          <span>Ver Laudo</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Cesta Consolidada de Exames Solicitados */}
                  <div className="space-y-2.5">
                    <span className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground block">
                      Cesta de Exames Solicitados ({examCount})
                    </span>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {exam.exams && exam.exams.length > 0 ? (
                        exam.exams.map((item, idx) => (
                          <div
                            key={idx}
                            className="bg-muted/30 border border-border/80 rounded-xl p-3.5 hover:border-primary/40 transition-all flex flex-col justify-between"
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-extrabold flex items-center justify-center shrink-0">
                                  {idx + 1}
                                </span>
                                <span className="font-bold text-sm text-foreground">
                                  {item.examName}
                                </span>
                              </div>

                              {item.justification && (
                                <p className="text-xs text-muted-foreground mt-1.5 pl-7">
                                  <strong>Indicação:</strong> {item.justification}
                                </p>
                              )}
                            </div>

                            {item.observations && (
                              <p className="text-[11px] text-primary font-medium mt-2 pl-7 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {item.observations}
                              </p>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="bg-muted/30 border border-border/80 rounded-xl p-3.5">
                          <p className="font-bold text-sm text-foreground">{exam.examType}</p>
                          {exam.instructions && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {exam.instructions}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Preparo e Orientações Médicas */}
                    {exam.instructions && (
                      <div className="p-3.5 bg-muted/40 rounded-xl border border-border/70 mt-2">
                        <span className="text-xs font-bold text-foreground block">
                          Preparo e Orientações Médicas
                        </span>
                        <p className="text-xs text-muted-foreground mt-0.5">{exam.instructions}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-xl h-9"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>
          <span className="text-xs font-semibold text-muted-foreground px-3">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-xl h-9"
          >
            Próxima <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* MODAL DE COTAÇÃO E AGENDAMENTO DE LABORATÓRIOS PARCEIROS */}
      {quoteExam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header do Modal com Logo Medikei */}
            <div className="p-5 sm:p-6 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="Medikei"
                  className="h-8 w-auto drop-shadow-xs dark:brightness-110"
                />
                <div className="h-5 w-px bg-border hidden sm:block" />
                <div>
                  <h2 className="text-lg font-extrabold text-foreground">
                    Cotação & Agendamento Laboratorial
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Rede integrada DASA, Fleury, Sabin e Coleta Domiciliar Medikei
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setQuoteExam(null)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 sm:p-6 space-y-6">
              {scheduleSuccessProtocol ? (
                /* Tela de Sucesso */
                <div className="text-center py-8 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 flex items-center justify-center mx-auto shadow-sm">
                    <CheckCircle2 className="h-9 w-9" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">
                      Agendamento Confirmado!
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Seu pedido de exames foi encaminhado com sucesso ao laboratório parceiro.
                    </p>
                  </div>

                  <div className="p-4 bg-muted/40 border border-border rounded-xl max-w-md mx-auto text-left space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Protocolo:</span>
                      <strong className="font-mono text-primary font-bold">
                        {scheduleSuccessProtocol}
                      </strong>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Modalidade:</span>
                      <strong className="text-foreground">
                        {collectionType === 'DOMICILIAR'
                          ? 'Coleta em Casa (Home Care)'
                          : 'Posto de Coleta'}
                      </strong>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Data/Horário:</span>
                      <strong className="text-foreground">
                        {new Date(scheduledDate).toLocaleString('pt-BR')}
                      </strong>
                    </div>
                  </div>

                  <Button
                    onClick={() => setQuoteExam(null)}
                    className="rounded-xl px-6 font-bold"
                  >
                    Fechar
                  </Button>
                </div>
              ) : (
                /* Seleção de Modalidade, Laboratório e Confirmação */
                <>
                  {/* Resumo da Cesta */}
                  <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">
                        Cesta de Exames Solicitados ({quoteExam.exams?.length || 1})
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Solicitado por {quoteExam.doctorName}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                      {quoteExam.exams && quoteExam.exams.length > 0 ? (
                        quoteExam.exams.map((it, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="bg-card font-semibold text-xs py-1 px-2.5 border-border"
                          >
                            {it.examName}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="outline" className="bg-card font-semibold text-xs py-1 px-2.5">
                          {quoteExam.examType}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Seleção do Tipo de Coleta */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2.5">
                      Modalidade de Coleta
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCollectionType('DOMICILIAR')}
                        className={`p-4 rounded-xl border text-left transition-all flex items-start gap-3 ${
                          collectionType === 'DOMICILIAR'
                            ? 'border-primary bg-primary/10 ring-1 ring-primary shadow-xs'
                            : 'border-border bg-card hover:bg-muted/40'
                        }`}
                      >
                        <Home className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold text-sm text-foreground">Coleta em Casa</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Enfermeiro Medikei vai até sua residência no horário agendado.
                          </p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setCollectionType('POSTO_COLETA')}
                        className={`p-4 rounded-xl border text-left transition-all flex items-start gap-3 ${
                          collectionType === 'POSTO_COLETA'
                            ? 'border-primary bg-primary/10 ring-1 ring-primary shadow-xs'
                            : 'border-border bg-card hover:bg-muted/40'
                        }`}
                      >
                        <Building2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold text-sm text-foreground">Posto de Coleta</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Compareça à unidade física mais próxima do laboratório escolhido.
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Comparativo de Laboratórios */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Selecione o Laboratório Parceiro (Orçamento da Cesta Completa)
                      </label>
                      <span className="text-xs text-muted-foreground">
                        Valores calculados para todos os exames
                      </span>
                    </div>

                    {isLoadingQuote ? (
                      <div className="p-8 text-center bg-muted/20 border border-border rounded-xl">
                        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground font-semibold">
                          Calculando valores em tempo real na rede parceira...
                        </p>
                      </div>
                    ) : isErrorQuote || !quoteData?.laboratorios?.length ? (
                      <div className="p-6 text-center bg-muted/20 border border-border rounded-xl">
                        <p className="text-sm font-semibold text-foreground">
                          Rede laboratorial disponível em regime de agendamento assistido.
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Nossa central encaminhará os dados do pedido aos laboratórios credenciados.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {quoteData.laboratorios.map((lab: LabQuoteItem) => {
                          const isSelected = selectedLabId === lab.laboratorioId;
                          const precoFinal =
                            collectionType === 'DOMICILIAR'
                              ? lab.valorTotalComDomiciliar
                              : lab.valorTotalExames;

                          return (
                            <div
                              key={lab.laboratorioId}
                              onClick={() => setSelectedLabId(lab.laboratorioId)}
                              className={`p-4 rounded-xl border transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                                isSelected
                                  ? 'border-primary bg-primary/10 ring-2 ring-primary shadow-sm'
                                  : 'border-border bg-card hover:border-primary/40'
                              }`}
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-bold text-sm text-foreground">
                                    {lab.nomeFantasia}
                                  </h4>
                                  {lab.coberturaCompleta && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] font-bold bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                                    >
                                      100% dos exames cobertos
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3 text-primary" />
                                    Laudo em até {lab.prazoResultadoHoras} horas
                                  </span>
                                  {lab.aceitaDomiciliar && (
                                    <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                      <Home className="h-3 w-3" /> Coleta Domiciliar Ativa
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="text-left sm:text-right shrink-0">
                                <span className="text-xs text-muted-foreground block">
                                  {collectionType === 'DOMICILIAR'
                                    ? 'Valor total (com taxa domiciliar):'
                                    : 'Valor total dos exames:'}
                                </span>
                                <span className="text-lg font-extrabold text-foreground text-primary">
                                  {formatCurrency(precoFinal)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Agendamento de Data & Horário */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Data e Horário Desejados
                      </label>
                      <Input
                        type="datetime-local"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="rounded-xl h-11"
                      />
                    </div>

                    {collectionType === 'DOMICILIAR' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                          CEP para Coleta Domiciliar
                        </label>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                          <Input
                            placeholder="00000-000"
                            value={address.cep}
                            onChange={(e) => setAddress((prev) => ({ ...prev, cep: e.target.value }))}
                            className="rounded-xl h-11"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botão Final de Confirmação */}
                  <div className="pt-3 border-t border-border flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setQuoteExam(null)}
                      className="rounded-xl h-11 px-5"
                    >
                      Cancelar
                    </Button>

                    <Button
                      onClick={() => scheduleMutation.mutate()}
                      disabled={!selectedLabId || scheduleMutation.isPending}
                      className="gap-2 rounded-xl h-11 px-6 font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm shadow-primary/20"
                    >
                      {scheduleMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Agendando...</span>
                        </>
                      ) : (
                        <>
                          <span>Confirmar Agendamento</span>
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
